import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface Permissions {
  canEdit: boolean;
  canDelete: boolean;
  canShare: boolean;
  role: 'admin' | 'crew' | 'client';
}

export interface PermissionsState {
  permissions: Permissions;
  loading: boolean;
}

export function usePermissions(eventId: string | null): PermissionsState {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Permissions>({
    canEdit: false,
    canDelete: false,
    canShare: false,
    role: 'client',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !eventId) {
      setPermissions({
        canEdit: false,
        canDelete: false,
        canShare: false,
        role: 'client',
      });
      setLoading(false);
      return;
    }

    const checkPermissions = async () => {
      setLoading(true);
      try {
        const { data: event } = await supabase
          .from('events')
          .select('created_by')
          .eq('id', eventId)
          .maybeSingle();

        const isOwner = event?.created_by === user.id;

        const { data: crewMember } = await supabase
          .from('crew_members')
          .select('permission_level, user_id, email')
          .eq('event_id', eventId)
          .or(`user_id.eq.${user.id},email.eq.${user.email}`)
          .maybeSingle();

        const isEditor = crewMember?.permission_level === 'editor';
        const isCrewMember = !!crewMember;

        setPermissions({
          canEdit: isOwner || isEditor,
          canDelete: isOwner,
          canShare: isOwner,
          role: isOwner ? 'admin' : isCrewMember ? 'crew' : 'client',
        });
      } finally {
        setLoading(false);
      }
    };

    checkPermissions();
  }, [user, eventId]);

  return { permissions, loading };
}
